import {
  WidgetMessage,
  ModalCloseMessage,
  ModalOpenMessage,
  FeatureSelectMessage,
  FeatureUnselectMessage,
  LanguageChangeMessage,
  FlyoutToggleMessage,
  InterfaceHideMessage,
  InterfaceResetMessage,
  NavigationVoiceStartMessage,
  NavigationPageStructureMessage,
  ColorChangeBackgroundMessage,
  ColorChangeTextMessage,
  MouseMoveMessage,
  isValidWidgetMessage,
  InterfaceShowMessage
} from '../types/message';
import { MessageActions } from '../types/message.actions';
import { MessageDomains } from '../types/message.domain';
import { Feature } from '../constants/features';

/**
 * Core message posting function that sends messages to the parent window.
 * All messages are validated to ensure they conform to the WidgetMessage interface.
 */
export const postMessage = (message: WidgetMessage): void => {
  if (!isValidWidgetMessage(message)) {
    console.error('Invalid widget message:', message);
    return;
  }
  
  window.parent.postMessage(message, '*');
};

// ===== MODAL DOMAIN MESSAGES =====

/**
 * Posts a modal close message
 */
export const postModalCloseMessage = (): void => {
  const message: ModalCloseMessage = {
    type: MessageDomains.MODAL,
    action: MessageActions.CLOSE,
  };
  postMessage(message);
};

/**
 * Posts a modal open message
 */
export const postModalOpenMessage = (modalId?: string): void => {
  const message: ModalOpenMessage = {
    type: MessageDomains.MODAL,
    action: MessageActions.OPEN,
    ...(modalId && { modalId }),
  };
  postMessage(message);
};

// ===== FEATURE DOMAIN MESSAGES =====

/**
 * Posts a feature select message
 */
export const postFeatureSelectMessage = (feature: Feature, data?: Record<string, unknown>): void => {
  // Debug log and trace
  console.log('postFeatureSelectMessage', feature, data);
  console.trace();

  const message: FeatureSelectMessage = {
    type: MessageDomains.FEATURE,
    action: MessageActions.SELECT,
    feature,
    ...(data && { data }),
  };
  postMessage(message);
};

/**
 * Posts a feature unselect message
 */
export const postFeatureUnselectMessage = (feature: Feature): void => {
  const message: FeatureUnselectMessage = {
    type: MessageDomains.FEATURE,
    action: MessageActions.UNSELECT,
    feature,
  };
  postMessage(message);
};

// ===== LANGUAGE DOMAIN MESSAGES =====

/**
 * Posts a language change message
 */
export const postLanguageChangeMessage = (language: string): void => {
  const message: LanguageChangeMessage = {
    type: MessageDomains.LANGUAGE,
    action: MessageActions.CHANGE,
    language,
  };
  postMessage(message);
};

// ===== FLYOUT DOMAIN MESSAGES =====

/**
 * Posts a flyout toggle message
 */
export const postFlyoutToggleMessage = (isOpen: boolean): void => {
  const message: FlyoutToggleMessage = {
    type: MessageDomains.FLYOUT,
    action: MessageActions.TOGGLE,
    isOpen,
  };
  postMessage(message);
};

// ===== INTERFACE DOMAIN MESSAGES =====

/**
 * Posts an interface hide message
 */
export const postInterfaceHideMessage = (): void => {
  const message: InterfaceHideMessage = {
    type: MessageDomains.INTERFACE,
    action: MessageActions.HIDE,
  };
  postMessage(message);
};

/**
 * Posts an interface show message
 */
export const postInterfaceShowMessage = (): void => {
  const message: InterfaceShowMessage = {
    type: MessageDomains.INTERFACE,
    action: MessageActions.SHOW,
  };
  postMessage(message);
};


/**
 * Posts an interface reset message
 */
export const postInterfaceResetMessage = (): void => {
  const message: InterfaceResetMessage = {
    type: MessageDomains.INTERFACE,
    action: MessageActions.RESET,
  };
  postMessage(message);
};

// ===== NAVIGATION DOMAIN MESSAGES =====

/**
 * Posts a navigation voice start message
 */
export const postNavigationVoiceStartMessage = (languageCode?: string): void => {
  const message: NavigationVoiceStartMessage = {
    type: MessageDomains.NAVIGATION,
    action: MessageActions.VOICE_START,
    ...(languageCode && { languageCode }),
  };
  postMessage(message);
};

/**
 * Posts a navigation page structure message
 */
export const postNavigationPageStructureMessage = (): void => {
  const message: NavigationPageStructureMessage = {
    type: MessageDomains.NAVIGATION,
    action: MessageActions.PAGE_STRUCTURE,
  };
  postMessage(message);
};

// ===== COLOR DOMAIN MESSAGES =====

/**
 * Posts a color change background message
 */
export const postColorChangeBackgroundMessage = (color: string): void => {
  const message: ColorChangeBackgroundMessage = {
    type: MessageDomains.COLOR,
    action: MessageActions.CHANGE_BACKGROUND,
    color,
  };
  postMessage(message);
};

/**
 * Posts a color change text message
 */
export const postColorChangeTextMessage = (color: string): void => {
  const message: ColorChangeTextMessage = {
    type: MessageDomains.COLOR,
    action: MessageActions.CHANGE_TEXT,
    color,
  };
  postMessage(message);
};

// ===== MOUSE DOMAIN MESSAGES =====

/**
 * Posts a mouse move message
 */
export const postMouseMoveMessage = (x: number, y: number): void => {
  const message: MouseMoveMessage = {
    type: MessageDomains.MOUSE,
    action: MessageActions.MOVE,
    coordinates: { x, y },
  };
  postMessage(message);
};


// ===== CONVENIENCE FUNCTIONS =====

/**
 * Toggles a feature on/off based on current selection state
 */
export const postFeatureToggleMessage = (
  feature: Feature, 
  isCurrentlySelected: boolean, 
  data?: Record<string, unknown>
): void => {
  if (isCurrentlySelected) {
    postFeatureUnselectMessage(feature);
  } else {
    postFeatureSelectMessage(feature, data);
  }
};

/**
 * Posts slider change message (used for features with multiple states)
 */
export const postSliderChangeMessage = (
  feature: Feature,
  slideValue: Feature,
  isDefault: boolean,
  data?: Record<string, unknown>
): void => {
  if (isDefault) {
    postFeatureUnselectMessage(feature);
  } else {
    postFeatureSelectMessage(slideValue, data);
  }
};

